//client\src\components\CommentSection.js
import React, { useState } from 'react';
import { authFetch, getAccessToken } from '../../Utilities/Api';
import './CommentSection.css';

const CommentSection = ({ postId, comments, setComments, postVisibility }) => {
    const [commentText, setCommentText] = useState('');
    const [loading, setLoading] = useState(false);

    // Get auth status and user details from localStorage
    const isAuthenticated = !!getAccessToken();
    const currentUserId = localStorage.getItem('userId');
    const currentUsername = localStorage.getItem('username') || 'You'; // Use 'username' key from localStorage

    // ---------------------------------------------------------
    // Function to add a comment (Uses Optimistic Update)
    // ---------------------------------------------------------
    const addComment = async (e) => {
        e.preventDefault();
        const trimmed = commentText.trim();

        // Guard clauses
        if (!trimmed || !isAuthenticated || loading) return;

        // 1. Create a temporary comment object for immediate display (Optimistic Update)
        const newCommentTemp = {
            // Using Date.now() as a temporary unique ID
            _id: Date.now(),
            text: trimmed,
            // Populate the user object required by the display logic
            user: {
                _id: currentUserId,
                username: currentUsername,
            },
            createdAt: new Date().toISOString()
        };

        // Optimistically update the UI: Add the temporary comment to the list
        setComments(prev => [newCommentTemp, ...prev]);
        setCommentText(''); // Clear the input right away
        setLoading(true);

        try {
            const res = await authFetch(`/profile-posts/comment/${postId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: trimmed }),
            });

            if (!res.ok) {
                // If the post failed, revert the optimistic update by removing the temp comment
                setComments(prev => prev.filter(c => c._id !== newCommentTemp._id));

                if (res.status === 403) {
                    alert("You are not authorized to comment on this post.");
                } else {
                    console.error(`❌ Failed with status ${res.status}`);
                }
                return;
            }

            // Success! In a production app, you would typically fetch the real comment
            // here. Since your backend returns only a success message, we rely on the
            // optimistic comment being structurally correct for the next refresh.

        } catch (err) {
            // If fetch failed completely, revert the optimistic update
            setComments(prev => prev.filter(c => c._id !== newCommentTemp._id));
            console.error('❌ Failed to add comment:', err.message);
        } finally {
            setLoading(false);
        }
    };

    const isCommentingAllowed = isAuthenticated;

    return (
        <div className="comment-section">
            {isCommentingAllowed ? (
                <form onSubmit={addComment}>
                    <input
                        type="text"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Write a comment..."
                        disabled={loading}
                    />
                    <button type="submit" disabled={loading || !commentText.trim()}>
                        {loading ? 'Posting...' : 'Post'}
                    </button>
                </form>
            ) : (
                <p className="comment-login-prompt">
                    Please <a href="/login">log in</a> to comment on posts.
                </p>
            )}

            <div className="comment-list">
                {comments.length > 0 ? (
                    comments.map((comment) => (
                        // Key should ideally be the real MongoDB _id
                        <div key={comment._id || comment.text} className="comment">
                            {/* 🟢 FIX APPLIED HERE: Safely access username from common backend structures */}
                            <span className="comment-author">
                                {/* Check: 1. Top-level username, 2. Nested user.username, 3. Nested user.name, 4. Fallback */}
                                {comment.username || comment.user?.username || comment.user?.name || 'Guest'}:
                            </span>
                            <span className="comment-text">{comment.text}</span>
                        </div>
                    ))
                ) : (
                    <p className="no-comments">No comments yet.</p>
                )}
            </div>
        </div>
    );
};

export default CommentSection;